import React from 'react';
import { Box, CircularProgress, Pagination } from '@mui/material';
import { 
    Grid, 
    Card, 
    CardContent, 
    Typography, 
    Button,
    IconButton
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface IActivity {
    id: number;
    title: string;
    courseName: string;
    category: string;
    completedOn: string;
    activityUrl: string;
    activityImage: string;
    modname: string;
}

interface ActivitiesCompletedProps {
    activities: any[];
    loading: boolean;
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
    };
    onPageChange: (page: number) => void;
}

const ActivitiesCompleted = ({ 
    activities, 
    loading,
    pagination,
    onPageChange 
}: ActivitiesCompletedProps) => {
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(value);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ position: 'relative', mb: 3 }}>
                {pagination.currentPage > 1 && (
                    <IconButton 
                        onClick={() => onPageChange(pagination.currentPage - 1)}
                        sx={{
                            position: 'absolute',
                            left: -20,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'background.paper' }
                        }}
                    >
                        <ChevronLeft />
                    </IconButton>
                )}

                {pagination.currentPage < pagination.totalPages && (
                    <IconButton 
                        onClick={() => onPageChange(pagination.currentPage + 1)}
                        sx={{
                            position: 'absolute',
                            right: -20,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'background.paper' }
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                )}

                <Grid container spacing={3}>
                    {activities.map((activity) => (
                        <Grid item xs={12} sm={6} md={3} key={activity.id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    bgcolor: '#F8F9FA',
                                    boxShadow: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                <CardContent sx={{ 
                                    flex: 1, 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    p: 3
                                }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        mb: 2,
                                        '& img': {
                                            width: '32px',
                                            height: '32px'
                                        }
                                    }}>
                                        <img 
                                            src={activity.activityImage} 
                                            alt={activity.modname}
                                        />
                                    </Box>
                                    <Typography 
                                        variant="h6" 
                                        align="center" 
                                        sx={{ 
                                            mb: 2,
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            lineHeight: '24px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            height: '48px'
                                        }}
                                    >
                                        {activity.title}
                                    </Typography>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography 
                                            variant="body2" 
                                            color="textSecondary"
                                            sx={{ mb: 1 }}
                                        >
                                            Course: {activity.courseName}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="textSecondary"
                                            sx={{ mb: 1 }}
                                        >
                                            Category: {activity.category}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="textSecondary"
                                        >
                                            Completed on: {activity.completedOn}
                                        </Typography>
                                    </Box>
                                    <Button 
                                        variant="contained" 
                                        href={activity.activityUrl}
                                        target="_blank"
                                        sx={{ 
                                            mt: 2,
                                            bgcolor: '#FF4D00',
                                            '&:hover': {
                                                bgcolor: '#cc3d00'
                                            },
                                            textTransform: 'none',
                                            borderRadius: '4px',
                                            height: '40px'
                                        }}
                                    >
                                        Go
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={pagination.totalPages}
                        page={pagination.currentPage}
                        onChange={handlePageChange}
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    );
};

export default ActivitiesCompleted; 